import {useEffect, useState} from 'react';
import {Radiobox} from '@mayo/mayo-ui-gakki';
import Layout from '../components/Layout';
import React, {HTMLAttributes, useContext} from 'react';
import {toast} from 'react-toastify';
import TextDialog from '@gakki/components/Dialog/TextDialog';
import Button, {ButtonGroup} from '@common/components/Button';
import AppContext from '../context/app-context';
import ProgressBoard from '../components/ProgressBoard';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {useAuthContext, useClient} from '../context/auth-context';
import cx from 'classnames';
import {ReactComponent as Flowchat01} from '@common/assets/flowchat01.svg';
import {ReactComponent as Flowchat02} from '@common/assets/flowchat02.svg';
import {useCompanyContext} from '@gakki/context/company-context';

const Form: React.FC<HTMLAttributes<HTMLDivElement> & { enablePY: boolean }> = ({
  children,
  className,
  enablePY,
  ...props
}) => {
  return (
    <div
      {...props}
      className={cx(
        'min-h-[610px] bg-white rounded border border-[#e3e4e6] p-[13px] pt-0 m-auto', {
          'w-[1015px]': enablePY,
          'w-[850px]': !enablePY,
        },
        className,
      )}
    >
      {children}
    </div>
  );
};

const FormTitle: React.FC<HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={cx(
        'text-h3 font-black !leading-[61px] border-b border-[#e9eced]',
        className,
      )}
    >
      {children}
    </div>
  );
};

export const STEP = 0;
export default function StepZero() {
  const queryClient = useQueryClient();
  const [showTextDialog, setShowTextDialog] = useState(false);
  const [value, setValue] = useState("1");
  const [step, setStep] = useState(0);
  const api = useClient();
  const {companyId} = useAuthContext();
  const {changeStep, setIsLoading, setIsDefault} = useContext(AppContext);
  const {enablePY} = useCompanyContext();
  const handleNext = () => {
    setShowTextDialog(true);
  };
  const handleConfirm = (s: string) => () => {
    const step = parseInt(s, 10);
    setStep(step);
  };

  useEffect(() => {
    if ([1, 12].includes(step)) {
      setIsLoading(true);
      setDefault();
    }
  }, [step]);

  const {mutate: setDefault} = useMutation(
    () => api.put(`company/default/${companyId}?toProgressState=${step}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('employee-list');
        setIsLoading(false);
        setIsDefault(true);
        changeStep(step);
      },
      onError: (error: {code: number; message: string}) => {
        setIsLoading(false);
        changeStep(1);
        toast.error(error.message || '設定資料異常', {
          hideProgressBar: true,
          position: 'top-center',
          autoClose: 3000,
        });
      },
    },
  );

  return (
    <React.Fragment>
      <Form enablePY={enablePY}>
        <FormTitle>開始您的導入流程</FormTitle>
        <div className={'flex items-center py-5 pt-[46.6px] text-[15px]'}>
          <div>
            <p className={'mb-10'}>
              Hi,
              <br />
              歡迎進入Apollo
              XE的快速導入流程，接下來我們將帶領您逐步完成系統專區的建置!
              <br />
              <br />
              <br />
              您將經過以下四個階段，當資料都填寫完畢，專區就建置完成囉!
              <br />
            </p>
            <ProgressBoard enablePY={enablePY}/>
          </div>
        </div>
      </Form>
      <TextDialog
        isOpen={showTextDialog}
        onConfirm={() => setShowTextDialog(false)}
        onCancel={() => setShowTextDialog(false)}
        title={'系統訊息'}
        content={
          <div className="w-full flex flex-col items-start mb-5">
            <div className="flex flex-col items-start text-base !leading-[22px]">
              <span>請先設置「導入精靈」資料，再啟用「XE 系統」。</span>
              <span>導入精靈已為您帶入預設資料，您有 2 種路徑可選擇。</span>
            </div>
            <div className="flex flex-col items-start text-base !leading-[26px] mt-5">
              <Radiobox.Group
                onChange={(e: any) => setValue(e.target.value)}
                name="test"
                defaultValue={value}
              >
                <Radiobox
                  className="mr-5"
                  name="text-radiobox1"
                  value="1"
                >
                  開始設置：依步驟編輯資料
                </Radiobox>
                <Flowchat01 className='ml-5 mt-[10px]' />
                <Radiobox
                  className="mt-5"
                  name="text-radiobox2"
                  value="12"
                >
                  立即啟用專區：以預設資料啟用專區，試用 XE 系統。
                </Radiobox>
                <Flowchat02 className='ml-5 mt-[10px]' />
              </Radiobox.Group>
            </div>
          </div>
        }
        footer={
          <ButtonGroup>
            <Button
              variant={'cta'}
              onClick={handleConfirm(value)}
              className={'cancel-btn'}
              data-test-id={'cancel-btn'}
              aria-label={'cancel-btn'}
            >
              送出
            </Button>
          </ButtonGroup>
        }
      />
      <Layout.Footer
        showUpdatedTime={false}
        showNextButton
        onNext={handleNext}
      />
    </React.Fragment>
  );
}
